import { useEffect, useRef, useState } from "react";
import type { TemplateField } from "@/api/researchCms.service";
import { templateFieldRows } from "../../utils/templateFieldRows";
import styles from "./TemplateFieldsPreview.module.css";

export type TemplateFieldsPreviewProps = {
  fields: TemplateField[];
  loading: boolean;
  error: string | null;
};

export function TemplateFieldsPreview({ fields, loading, error }: TemplateFieldsPreviewProps) {
  const [expanded, setExpanded] = useState(false);
  const rows = templateFieldRows(fields);
  const toggleRef = useRef<HTMLButtonElement>(null);

  /**
   * Keeps the control on screen when the section shuts.
   *
   * This is the last section on the page, so collapsing it shortens the document by the height of the
   * rows. Anyone who had scrolled down to read them is left at an offset that no longer exists and the
   * browser drops them at the new bottom, with the control they just pressed somewhere off screen.
   *
   * It runs in an effect rather than off the click, because the scroll has to happen AFTER the rows have
   * gone - measured from a `requestAnimationFrame` in the handler it fired while the page was still its
   * old height and the toggle ended up 744px away regardless.
   */
  const wasExpanded = useRef(false);
  useEffect(() => {
    const justCollapsed = wasExpanded.current && !expanded;
    wasExpanded.current = expanded;
    if (!justCollapsed) return;

    const toggle = toggleRef.current;
    if (!toggle) return;
    const { top, bottom } = toggle.getBoundingClientRect();
    const offScreen = top < 0 || bottom > window.innerHeight;
    if (offScreen) toggle.scrollIntoView({ block: "center" });
  }, [expanded]);

  return (
    <section className={styles.section}>
      <button
        type="button"
        ref={toggleRef}
        className={styles.toggle}
        aria-expanded={expanded}
        onClick={() => setExpanded(current => !current)}
      >
        <span>Template fields ({rows.length} publishable submission fields)</span>
        <span
          className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ""}`}
          aria-hidden="true"
        >
          ▶
        </span>
      </button>

      {error !== null && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      {/* Stays mounted so the collapse can animate. Unmounting made 744px of rows vanish in a frame and
          snapped the scroll up with it, since this is the last section on the page. `expanded` gates the
          accessibility instead of the presence: nothing here is announced or focusable while it is shut. */}
      <div
        className={`${styles.reveal} ${expanded ? styles.revealOpen : ""}`}
        data-testid="template-fields-reveal"
        aria-hidden={!expanded}
        inert={!expanded}
      >
        {error === null && loading && <p className={styles.loading}>Loading template fields…</p>}

        {error === null && !loading && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeadRow}>
                  <th className={styles.tableHeadCell}>Label</th>
                  <th className={styles.tableHeadCell}>Field name</th>
                  <th className={styles.tableHeadCell}>Type</th>
                  <th className={styles.tableHeadCell}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.fieldName} className={styles.tableRow}>
                    <td className={styles.tableCell}>{row.fieldLabel}</td>
                    <td className={styles.tableCell}>{row.fieldName}</td>
                    <td className={styles.tableCell}>{row.fieldType}</td>
                    <td className={styles.tableCell}>
                      <span className={styles.badges}>
                        {row.publishable && <span className={styles.badge}>Publishable</span>}
                        {row.editable && <span className={styles.badge}>Editable</span>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
